import { BehaviorSubject } from "rxjs";

import { Store } from "./store";

export type RecoverableFeedKey = "workOrderTypes" | "pollCategories" | "users";

export type RecoverableFeedError = {
  id: string;
  feed: RecoverableFeedKey;
  status?: number;
  retry: () => void;
  sequence?: number;
};

type RecoverableFeedErrors = Record<string, RecoverableFeedError>;
type RecoverableFeedErrorReport = Omit<RecoverableFeedError, "sequence">;

export function selectLatestRecoverableFeedErrors(
  errors: RecoverableFeedErrors | null | undefined
): RecoverableFeedError[] {
  const byFeed = new Map<RecoverableFeedKey, RecoverableFeedError>();
  Object.values(errors ?? {}).forEach((error) => {
    const current = byFeed.get(error.feed);
    if (!current || (error.sequence ?? 0) > (current.sequence ?? 0)) {
      byFeed.set(error.feed, error);
    }
  });
  return [...byFeed.values()];
}

export class RecoverableFeedStore extends Store<RecoverableFeedErrors> {
  protected _observable = new BehaviorSubject<RecoverableFeedErrors | null>({});
  private static instance?: RecoverableFeedStore;
  private retryCallbacks = new Map<string, () => void>();
  private reportSequence = 0;

  static getInstance(): RecoverableFeedStore {
    RecoverableFeedStore.instance =
      RecoverableFeedStore.instance ?? new RecoverableFeedStore();
    return RecoverableFeedStore.instance;
  }

  private constructor() {
    super();
  }

  report(error: RecoverableFeedErrorReport): void {
    this.retryCallbacks.set(error.id, error.retry);
    const storedError: RecoverableFeedError = {
      ...error,
      sequence: ++this.reportSequence,
      retry: () => this.retry(error.id)
    };
    this.addWithMapper((current) => ({
      ...(current ?? {}),
      [error.id]: storedError
    }));
  }

  retry(id: string): void {
    this.retryCallbacks.get(id)?.();
  }

  clear(id: string): void {
    this.retryCallbacks.delete(id);
    this.addWithMapper((current) => {
      const next = { ...(current ?? {}) };
      delete next[id];
      return next;
    });
  }

  clearAll(): void {
    this.retryCallbacks.clear();
    this.add({});
  }
}
